import type { Vec3 } from '../../geo/types';
import type { MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import type { CastleTier } from './shipTemplates';
import {
  CASTLE_RAIL,
  WALE_HALF,
  WATERLINE,
  castleTiers,
  deckAt,
  hullPoint,
  railAt,
  tierRailAt,
  type FormedTemplate,
} from './shipForm';
import {
  RAIL_INSET,
  castleHalfWidth,
  samplesOver,
  type HullStyles,
  type ShipTier,
} from './hullParts';
import { loftSolid, sparSolid } from './shipSolids';
import { timberBeam, timberBox, timberPost } from './shipTimber';
import { rope } from './rigParts';
import { rigReach } from './rigging';

/*
 * The fittings of a formed hull: the rudder and the anchor cable in every tier; the castles' open
 * rails on posts (R3's pale posts), the waist's gun ports (R34's pale dots; the woodcut's ports),
 * hatches and capstan in the mid and near tiers; the stowed bow anchors in the near tier.
 */

const RAIL = { size: 0.14, spacing: 0.7, post: 0.12, stem: 0.05 } as const;
const PORT = { maxHalf: 0.3, share: 0.35, depth: 0.15, proud: 0.04, opening: 0.62 } as const;
const RUDDER = {
  head: 0.9,
  foot: 1.4,
  topChord: 0.45,
  chord: 1.0,
  rakeShare: 0.9,
  half: 0.15,
} as const;
const CABLE = { at: 0.05, u: 0.8, reach: 0.8, below: 0.2, out: 0.8 } as const;
const HATCH = { length: 1.6, half: 0.8, sink: 0.1, rise: 0.3, spots: [0.35, 0.7] } as const;
const CAPSTAN = { radius: 0.35, sink: 0.1, rise: 0.9, sides: 8 } as const;
/* The bow anchors hang forward of the fore channels, whose shrouds come down from 0.12 of the
   length on the kraeck. */
const ANCHOR = {
  at: 0.07,
  u: 0.97,
  shank: 2.4,
  stock: 1.8,
  arm: 0.9,
  off: 0.25,
  bar: 0.07,
} as const;

/* The stretch a castle deck's side rails run over: up to the next aftercastle tier standing on
   it; a forecastle has none. */
function railSpan(template: FormedTemplate, castle: CastleTier): readonly [number, number] {
  const { aftcastle } = template.form;
  if (!aftcastle.includes(castle)) return [castle.from, castle.to];
  const above = aftcastle.filter((t) => t.rail > castle.rail && t.from > castle.from);
  const to = above.length > 0 ? Math.min(...above.map((t) => t.from)) : castle.to;
  return [castle.from, to];
}

/* Where a castle's rail runs at `s`, at its height, on the deck's edge just inside the wall. */
function edgePoint(template: FormedTemplate, castle: CastleTier, s: number, side: number): Vec3 {
  const x = hullPoint(template, s, 1)[0];
  const w = Math.max(RAIL.stem, castleHalfWidth(template, s) - RAIL_INSET);
  return [x, tierRailAt(template, castle, s), side * w];
}

/* Posts stand at every `spacing` along the rail's line, measured from its start, from the deck
   a rail height below the rail up to the rail; a line that meets another's corner leaves its
   first post to that one. */
function postsAlong(
  builder: MeshBuilder,
  style: VertexStyle,
  line: readonly Vec3[],
  skipFirst: boolean
): void {
  let carried = skipFirst ? RAIL.spacing : 0;
  for (let i = 0; i + 1 < line.length; i++) {
    const [a, b] = [line[i]!, line[i + 1]!];
    const run = Math.hypot(b[0] - a[0], b[2] - a[2]);
    for (let d = carried; d < run - (skipFirst ? RAIL.post : 0); d += RAIL.spacing) {
      const t = d / run;
      const rail = a[1] + (b[1] - a[1]) * t;
      const base: Vec3 = [a[0] + (b[0] - a[0]) * t, rail - CASTLE_RAIL, a[2] + (b[2] - a[2]) * t];
      timberPost(builder, style, base, CASTLE_RAIL - RAIL.size, RAIL.post);
    }
    carried = (((carried - run) % RAIL.spacing) + RAIL.spacing) % RAIL.spacing;
  }
}

/* A rail along a line of points at rail height, on its posts. */
function railAlong(
  builder: MeshBuilder,
  style: VertexStyle,
  line: readonly Vec3[],
  skipFirst = false
): void {
  const centre = (p: Vec3): Vec3 => [p[0], p[1] - RAIL.size / 2, p[2]];
  for (let i = 0; i + 1 < line.length; i++) {
    timberBeam(builder, style, centre(line[i]!), centre(line[i + 1]!), RAIL.size, RAIL.size);
  }
  postsAlong(builder, style, line, skipFirst);
}

/* A side rail stops half its width short of the deck's ends, so no corner of it stands out. */
function withinEnds(line: readonly Vec3[]): readonly Vec3[] {
  const last = line.length - 1;
  return line.map((p, i): Vec3 => {
    const inward = i === 0 ? 1 : i === last ? -1 : 0;
    return [p[0] + (inward * RAIL.size) / 2, p[1], p[2]];
  });
}

/* A rail across a castle deck's open end, set in by half its width towards the deck (`inward`
   along the hull) so it stands on the deck and within the hull's length. */
function crossRail(
  builder: MeshBuilder,
  template: FormedTemplate,
  style: VertexStyle,
  castle: CastleTier,
  end: { readonly s: number; readonly inward: number }
): void {
  const shift = (p: Vec3): Vec3 => [p[0] + (end.inward * RAIL.size) / 2, p[1], p[2]];
  const edge = (side: number): Vec3 => shift(edgePoint(template, castle, end.s, side));
  railAlong(builder, style, [edge(-1), edge(1)], true);
}

function buildCastleRails(
  builder: MeshBuilder,
  template: FormedTemplate,
  tier: ShipTier,
  style: VertexStyle
): void {
  const { forecastle, aftcastle } = template.form;
  for (const castle of castleTiers(template)) {
    const [from, to] = railSpan(template, castle);
    const samples = samplesOver(tier, from, to);
    for (const side of [1, -1]) {
      const line = samples.map((s) => edgePoint(template, castle, s, side));
      railAlong(builder, style, withinEnds(line));
    }
    const open =
      castle === forecastle ? { s: castle.to, inward: -1 } : { s: castle.from, inward: 1 };
    crossRail(builder, template, style, castle, open);
    if (castle === aftcastle[aftcastle.length - 1]) {
      crossRail(builder, template, style, castle, { s: castle.to, inward: -1 });
    }
  }
}

function portHalf(template: FormedTemplate, s: number): number {
  const { wales, ports } = template.form;
  const below = Math.max(...wales.filter((w) => w < ports.u)) + WALE_HALF;
  const above = Math.min(...wales.filter((w) => w > ports.u)) - WALE_HALF;
  const gap = hullPoint(template, s, above)[1] - hullPoint(template, s, below)[1];
  return Math.min(PORT.maxHalf, PORT.share * gap);
}

/* The hull is widest near the ports' height, so their faces stand out from its widest point. */
function outerAt(template: FormedTemplate, s: number, u: number, halfU: number): number {
  const probes = [-1, -0.5, 0, 0.5, 1].map((k) => hullPoint(template, s, u + k * halfU)[2]);
  return Math.max(...probes) + PORT.proud;
}

function buildPorts(builder: MeshBuilder, template: FormedTemplate, styles: HullStyles): void {
  const { u, from, to, spacing } = template.form.ports;
  const count = Math.floor(((to - from) * template.length) / spacing + 1e-9) + 1;
  for (let j = 0; j < count; j++) {
    const s = from + (j * spacing) / template.length;
    const a = portHalf(template, s);
    const [x, y, z] = hullPoint(template, s, u);
    const halfU = a / (railAt(template, s) - hullPoint(template, s, 0)[1]);
    const outer = outerAt(template, s, u, halfU);
    const b = PORT.opening * a;
    for (const side of [1, -1]) {
      const span = (lo: number, hi: number): readonly [number, number] =>
        side > 0 ? [lo, hi] : [-hi, -lo];
      const [z0, z1] = span(z - PORT.depth, outer);
      timberBox(builder, styles.rail, [x - a, y - a, z0], [x + a, y + a, z1]);
      const [w0, w1] = span(z - PORT.depth * 0.7, outer + 0.02);
      timberBox(builder, styles.port, [x - b, y - b, w0], [x + b, y + b, w1]);
    }
  }
}

/* The rudder hangs abaft the transom's foot, raked with it, from below the water to the tuck. */
function buildRudder(builder: MeshBuilder, template: FormedTemplate, style: VertexStyle): void {
  const tuck = hullPoint(template, 1, 0);
  const top = hullPoint(template, 1, 1);
  const slope = (top[0] - tuck[0]) / (top[1] - tuck[1]);
  const headY = tuck[1] + RUDDER.head;
  const footY = WATERLINE - RUDDER.foot;
  const headX = tuck[0] + 0.05;
  const footX = headX - slope * (headY - footY);
  const chord = Math.min(RUDDER.chord, RUDDER.rakeShare * template.form.transom.rake);
  const outline = (z: number): Vec3[] => [
    [footX + chord, footY, z],
    [footX, footY, z],
    [headX, headY, z],
    [headX + RUDDER.topChord, headY, z],
  ];
  loftSolid(builder, style, [outline(-RUDDER.half), outline(RUDDER.half)]);
}

/* From a hawse on the starboard bow, down into the water ahead, within the room the rig keeps. */
function buildAnchorCable(
  builder: MeshBuilder,
  template: FormedTemplate,
  style: VertexStyle
): void {
  const [x, y, z] = hullPoint(template, CABLE.at, CABLE.u);
  const end: Vec3 = [
    -template.length / 2 - CABLE.reach * rigReach(template).beyondStem,
    WATERLINE - CABLE.below,
    z + CABLE.out,
  ];
  rope(builder, style, [x, y, z], end);
}

function buildDeckGear(builder: MeshBuilder, template: FormedTemplate, style: VertexStyle): void {
  const fore = template.form.forecastle.to;
  const main = template.masts[0]!.position;
  for (const k of HATCH.spots) {
    const s = fore + (main - fore) * k;
    const x = hullPoint(template, s, 1)[0];
    const deck = deckAt(template, s);
    timberBox(
      builder,
      style,
      [x - HATCH.length / 2, deck - HATCH.sink, -HATCH.half],
      [x + HATCH.length / 2, deck + HATCH.rise, HATCH.half]
    );
  }
  const s = (main + template.form.aftcastle[0]!.from) / 2;
  const x = hullPoint(template, s, 1)[0];
  const deck = deckAt(template, s);
  sparSolid(builder, style, {
    from: [x, deck - CAPSTAN.sink, 0],
    to: [x, deck + CAPSTAN.rise, 0],
    radiusFrom: CAPSTAN.radius,
    radiusTo: CAPSTAN.radius,
    sides: CAPSTAN.sides,
    painterWidthScale: 1,
  });
}

/* A bow anchor stowed on each side, hung by its ring under the rail, shank down, arms up either side. */
function buildAnchors(builder: MeshBuilder, template: FormedTemplate, style: VertexStyle): void {
  const bar = (from: Vec3, to: Vec3): void => {
    sparSolid(builder, style, {
      from,
      to,
      radiusFrom: ANCHOR.bar,
      radiusTo: ANCHOR.bar,
      sides: 4,
      painterWidthScale: 1,
    });
  };
  for (const side of [1, -1]) {
    const [x, y] = hullPoint(template, ANCHOR.at, ANCHOR.u);
    const widest = Math.max(
      ...[0.5, 0.65, 0.8, ANCHOR.u].map((u) => hullPoint(template, ANCHOR.at, u)[2])
    );
    const z = side * (widest + ANCHOR.off);
    const crown: Vec3 = [x, y - ANCHOR.shank, z];
    bar([x, y, z], crown);
    bar([x - ANCHOR.stock / 2, y - 0.15, z], [x + ANCHOR.stock / 2, y - 0.15, z]);
    bar(crown, [x - ANCHOR.arm, y - ANCHOR.shank + ANCHOR.arm, z]);
    bar(crown, [x + ANCHOR.arm, y - ANCHOR.shank + ANCHOR.arm, z]);
  }
}

export function buildFittings(
  builder: MeshBuilder,
  template: FormedTemplate,
  tier: ShipTier,
  styles: HullStyles
): void {
  buildRudder(builder, template, styles.hull);
  buildAnchorCable(builder, template, styles.rope);
  if (tier === 'far') return;
  buildCastleRails(builder, template, tier, styles.rail);
  buildPorts(builder, template, styles);
  buildDeckGear(builder, template, styles.hull);
  if (tier === 'near') buildAnchors(builder, template, styles.rope);
}
